import React, { use } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';

import useResidentSettingsBulkEdit from 'features/residents/resident-settings/components/resident-settings-bulk-edit/hooks';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import OrganizationUnitScenarioIcon from '../organization-unit-scenario-icon/organization-unit-scenario-icon';
import ResidentSettingsBulkHeader from '../resident-settings-bulk-header/resident-settings-bulk-header';
import styles from './resident-settings-bulk-edit.module.scss';

const ResidentSettingsBulkEdit = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const {
    bulkEditState: { residentSettingBulkEditForm, availableScenariosIsLoading },
  } = use(ResidentSettingsProviderContext);

  const { handleSubmit } = residentSettingBulkEditForm;
  const { submitChanges, updateBulkScenarioIsActiveIsLoading } = useResidentSettingsBulkEdit();

  const renderScenarioRow = (index: number): React.JSX.Element => (
    <div className={styles.scenarioRowContainer}>
      {residentSettingBulkEditForm.getValues('bulkScenarioActiveStates').length > index && (
        <>
          <Controller
            name={`bulkScenarioActiveStates.${index}.isActive`}
            control={residentSettingBulkEditForm.control}
            render={({ field }) => <Switch {...field} type="checkbox" value={field.value} checked={field.value} />}
          />
          <OrganizationUnitScenarioIcon
            scenarioType={residentSettingBulkEditForm.getValues('bulkScenarioActiveStates')[index].scenarioType}
            isActive={residentSettingBulkEditForm.getValues('bulkScenarioActiveStates')[index].isActive}
          />
          <Typography variant="h5">
            {t(`details.${residentSettingBulkEditForm.getValues('bulkScenarioActiveStates')[index].scenarioType.toLowerCase()}`)}
          </Typography>
        </>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <FormProvider {...residentSettingBulkEditForm}>
        <form onSubmit={handleSubmit(submitChanges)}>
          <ResidentSettingsBulkHeader headerText={t('bulkEditScenarios.title')} isSubmitting={updateBulkScenarioIsActiveIsLoading} />
          <div className={styles.body}>
            <Grid className={styles.summaryContent}>
              <NotificationsNoneOutlined />
              <Typography variant="h5">{t('details.scenariosDividerText')}</Typography>
            </Grid>
            {availableScenariosIsLoading && <LoadingIndicator />}
            {!availableScenariosIsLoading && residentSettingBulkEditForm.getValues('bulkScenarioActiveStates').length === 0 && (
              <Typography variant="h5" className={styles.noScenarios}>
                {t('bulkEditScenarios.noScenariosAvailable')}
              </Typography>
            )}
            {!availableScenariosIsLoading && residentSettingBulkEditForm.getValues('bulkScenarioActiveStates').length > 0 && (
              <>
                {[...residentSettingBulkEditForm.getValues('bulkScenarioActiveStates')].map((scenario, index) => (
                  <React.Fragment key={scenario.scenarioType}>{renderScenarioRow(index)}</React.Fragment>
                ))}
              </>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ResidentSettingsBulkEdit;
